import Icon from "@afa-shared/afa-components/dist/Icon";
import clsx from "clsx";
import React, { ReactNode, RefObject, useId } from "react";

import styles from "./Message.module.css";

export type MessageType = "info" | "success" | "warning" | "error";

export interface IMessageProps {
  children?: ReactNode;
  type: MessageType;
  heading?: string;
  headingVariant?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "paragraph";
  linkType?: ReactNode;
  closable?: boolean;
  messageRef?: RefObject<HTMLDivElement | null>;
  closeOnClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  withoutIcon?: boolean;
  closeButtonLabel?: string;
  /**
   * Controls the ARIA live-region behaviour for dynamically rendered messages.
   *
   * Omit (default) for static CMS content so the message is not intrusive
   * (no role, no live region — replaces the old, incorrect role="alertdialog").
   * "polite"    -> role="status" (non-urgent info/success feedback)
   * "assertive" -> role="alert"  (errors/validation that must be announced)
   */
  live?: "polite" | "assertive";
  [htmlAttributes: string]: unknown;
}

const typeToIconName = {
  info: "info",
  success: "check_circle",
  error: "cancel",
  warning: "error",
} as const satisfies Record<MessageType, string>;

/**
 * Maps the opt-in `live` prop to its ARIA live-region attributes. Omitting
 * `live` (the default for static content) yields no role and no live region.
 */
const liveRegions = {
  polite: { role: "status", ariaLive: "polite" },
  assertive: { role: "alert", ariaLive: "assertive" },
} as const;

/**
 * Internal replacement for `@afa-shared/afa-components/dist/Message`.
 *
 * Fixes two accessibility problems in the shared component:
 * 1. It used `role="alertdialog"` for static, non-interactive text, which is
 *    overly intrusive. The role is removed by default; dynamic consumers can
 *    opt in to a polite/assertive live region via the `live` prop.
 * 2. Every instance shared the same hard-coded `aria-labelledby="message-title"`
 *    (and `aria-describedby="message-body"`), so instances lacked accessible
 *    names. Ids are now generated per instance with `useId()` and only
 *    referenced when the corresponding element is rendered.
 *
 * The component owns its styling via `Message.module.css`; the root keeps a
 * plain `afa-shared-message` class so the print stylesheet (`public/print.css`)
 * keeps working. Only the `Icon` primitive is still imported from the shared
 * library — the heading/body typography is rendered with native elements.
 */
const Message = ({
  children,
  type,
  heading,
  headingVariant = "h2",
  linkType,
  closable = false,
  messageRef,
  closeOnClick,
  className,
  withoutIcon = false,
  closeButtonLabel = "Stäng meddelande",
  live,
  ...htmlAttributes
}: IMessageProps) => {
  const reactId = useId();
  const headingId = `message-title-${reactId}`;
  const bodyId = `message-body-${reactId}`;

  const hasHeading = Boolean(heading);
  const hasBody = Boolean(children || linkType);

  const liveRegion = live ? liveRegions[live] : undefined;

  const renderHeading = () => {
    if (!heading) return null;

    if (headingVariant === "paragraph") {
      return (
        <span id={headingId} className={styles["heading-text"]}>
          {heading}
        </span>
      );
    }

    const HeadingTag = headingVariant;

    return (
      <HeadingTag id={headingId} className={styles.heading}>
        {heading}
      </HeadingTag>
    );
  };

  return (
    <div
      className={clsx(styles.message, styles.type, styles[type], "afa-shared-message", className)}
      ref={messageRef}
      role={liveRegion?.role}
      aria-live={liveRegion?.ariaLive}
      aria-labelledby={hasHeading ? headingId : undefined}
      aria-describedby={hasBody ? bodyId : undefined}
      {...htmlAttributes}
    >
      <div className={styles["message-top"]}>
        <div className={clsx(styles["heading-wrapper"], { [styles["has-body"]]: hasBody })}>
          {!withoutIcon && (
            <Icon
              name={typeToIconName[type]}
              size="sm"
              referenceSize="span"
              className={styles["heading-icon"]}
            />
          )}
          {renderHeading()}
        </div>
        {closable && (
          <button
            type="button"
            className={styles["close-button"]}
            onClick={closeOnClick}
            aria-label={closeButtonLabel}
          >
            <Icon name="close" className="close-button-icon" />
          </button>
        )}
      </div>
      {hasBody && (
        <div className={clsx(styles.content, { [styles["no-padding"]]: withoutIcon })} id={bodyId}>
          {children && <div className={styles["children-container"]}>{children}</div>}
          {linkType && <div className={styles["link-wrapper"]}>{linkType}</div>}
        </div>
      )}
    </div>
  );
};

export default Message;
